import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { permissionDeniedLabel } from "@/constants/permission";
import { DecisionKindEnum } from "@/enum/decision-kind";
import { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { CheckCircle, MoreHorizontal, XCircle } from "lucide-react";

import { PAYROLL_VOUCHER_LABELS } from "../messages";
import { PayrollVoucher, VoucherDecision } from "../types";
import { DecisionConfirmationModal } from "./decision-confirmation-modal";

export const ALREADY_DECIDED_TEST_ID: string = "voucher-already-decided";

interface VoucherRowActionsProps {
  voucher: PayrollVoucher;
  canDecide: boolean;
  onDecide: (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ) => Promise<void>;
}

const MODAL_TITLE: Record<DecisionKindEnum, string> = {
  [DecisionKindEnum.APPROVE]: PAYROLL_VOUCHER_LABELS.DECISION_TITLE_APPROVE,
  [DecisionKindEnum.REJECT]: PAYROLL_VOUCHER_LABELS.DECISION_TITLE_REJECT,
};

const MODAL_DESCRIPTION: Record<DecisionKindEnum, string> = {
  [DecisionKindEnum.APPROVE]:
    PAYROLL_VOUCHER_LABELS.DECISION_DESCRIPTION_APPROVE,
  [DecisionKindEnum.REJECT]:
    PAYROLL_VOUCHER_LABELS.DECISION_DESCRIPTION_REJECT,
};

export function VoucherRowActions({
  voucher,
  canDecide,
  onDecide,
}: VoucherRowActionsProps): JSX.Element {
  const { handleModal } = useDialogModal();

  const isPending: boolean =
    voucher.status === PayrollVoucherStatusEnum.PENDING;

  const labelFor = (action: string): string =>
    canDecide ? action : permissionDeniedLabel(action);

  const openDecision = (kind: DecisionKindEnum): void => {
    handleModal({
      isOpen: true,
      title: MODAL_TITLE[kind],
      description: MODAL_DESCRIPTION[kind],
      element: (
        <DecisionConfirmationModal
          voucher={voucher}
          kind={kind}
          onConfirm={(decision: VoucherDecision) => onDecide(voucher, decision)}
        />
      ),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          aria-label={PAYROLL_VOUCHER_LABELS.ROW_ACTIONS}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {isPending && (
          <DropdownMenuItem
            disabled={!canDecide}
            onClick={() => openDecision(DecisionKindEnum.APPROVE)}
          >
            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
            {labelFor(PAYROLL_VOUCHER_LABELS.APPROVE)}
          </DropdownMenuItem>
        )}

        {isPending && (
          <DropdownMenuItem
            disabled={!canDecide}
            onClick={() => openDecision(DecisionKindEnum.REJECT)}
          >
            <XCircle className="mr-2 h-4 w-4 text-red-600" />
            {labelFor(PAYROLL_VOUCHER_LABELS.REJECT)}
          </DropdownMenuItem>
        )}

        {!isPending && (
          <DropdownMenuItem disabled data-testid={ALREADY_DECIDED_TEST_ID}>
            <XCircle className="mr-2 h-4 w-4 text-gray-400" />
            {PAYROLL_VOUCHER_LABELS.ALREADY_DECIDED}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
